import React, { forwardRef, useCallback, useEffect, useRef } from 'react'
import { css, useTheme } from '@emotion/react'
import { useState } from 'react'
import { IoClose } from 'react-icons/io5'
import { MdArrowDropDown } from 'react-icons/md'
import { Link, MegaMenu, Accordion, Card } from '../base-ui'
import { useTranslation } from 'next-i18next'

function Nav({ items, type = 'default', onClose, visible = false }) {
    const theme = useTheme()
    const openRef = useRef({})
    const { t } = useTranslation()
    const menuItemStyle = `
        font-size: 1rem;
        font-weight: 500;
    `

    const subMenuStyle = `
        border-bottom: 2px solid ${theme.colors.secondary};
        padding: 0.25rem 0;
        width: fit-content;
        margin: 0.25rem 0;
        a {
            color: ${theme.colors.white};
            &:active {
                background-color: ${theme.colors.primary};
                color: ${theme.colors.white};            
            }
        }
    `
    const navColor = theme.colors.nav.color
    const navHoverColor = theme.colors.nav.hover

    const defaultNav = (
        <ul className="hidden lg:flex flex-wrap mb-0 items-center">
            <li className="mx-3">
                <div>
                    <Link
                        href="/"
                        hoverColor={navHoverColor}
                        color={navColor}
                        style={menuItemStyle}
                    >
                        {t('خانه')}
                    </Link>
                </div>
            </li>
            {items?.map((s, idx) => (
                <NavItem
                    key={idx}
                    navColor={navColor}
                    navHoverColor={navHoverColor}
                    link={s.link}
                    label={s.label}
                    items={s.children}
                    depth={s.maxDepth}
                    openRef={openRef}
                />
            ))}
        </ul>
    )
    const mobileNav = (
        <div
            className={`lg:hidden fixed w-screen h-screen -top-0 bg-gray-600 bg-opacity-20 z-20 backdrop-filter backdrop-blur-sm ${visible ? 'block' : 'hidden'
                }`}
            css={css`
                right: 0;
            `}
        >
            <div
                className={`mobile-nav slideMenu w-52 bg-white float-left px-6 h-full overflow-y-auto 
            ${visible ? 'slideEnter' : 'hidden'}`}
            >

                <button
                    className="mt-12 flex items-center gap-2 close-btn"
                    css={css`
                        color: ${navColor};
                    `}
                    onClick={onClose}
                >
                    <IoClose className="text-2xl" />
                    <span className="text-md">{t('بستن')}</span>
                </button>
                <ul className="pt-6">
                    {items?.map((s, idx) => {
                        if (s.children?.length)
                            return (
                                <li key={idx} className="mb-6">
                                    <Accordion
                                        key={idx}
                                        title={s.label}
                                        content={
                                            <div
                                                css={css`
                                                    display: flex;
                                                    flex-direction: column;
                                                `}
                                            >
                                                <Link
                                                    key={idx}
                                                    href={s.link}
                                                    onClick={onClose}
                                                    style={subMenuStyle}
                                                    color={navColor}
                                                >
                                                    {s.label}
                                                </Link>
                                                {s.children.map((item, idx) => (
                                                    <Link
                                                        key={idx}
                                                        href={item.link}
                                                        onClick={onClose}
                                                        style={subMenuStyle}
                                                        color={navColor}
                                                    >
                                                        {item.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        }
                                    />
                                </li>
                            )
                        return (
                            <li key={idx} className="mb-6">
                                <Link
                                    href={s.link}
                                    onClick={onClose}
                                    style="display: block; width: 100%"
                                    color={navColor}
                                >
                                    <span
                                        className="block outline outline-offset-4 outline-1 text-start rounded px-2 py-1 w-full bg-gray-100"
                                        css={css`
                                            outline-color: ${navColor};
                                        `}
                                    >
                                        {s.label}
                                    </span>
                                </Link>
                            </li>
                        )
                    })}
                </ul>
            </div>
        </div>
    )

    if (type === 'default') return defaultNav
    return mobileNav
}

export default Nav

function NavItem({
    navColor,
    navHoverColor,
    link,
    label,
    items,
    openRef,
    depth
}) {
    const [isHovering, setIsHovering] = useState(false)
    const elRef = useRef()
    const handleMouseOver = () => {
        if (
            openRef?.current?.setIsHovering &&
            openRef.current.setIsHovering !== setIsHovering
        )
            openRef.current.setIsHovering(false)
        setIsHovering(true)
        if (openRef) openRef.current.setIsHovering = setIsHovering
    }
    const wrapperRef = useRef('menu')
    const outsideClickAlert = useCallback(() => {
        setIsHovering(false)
    }, [])
    useEffect(() => {
        function handleClickOutside(event) {
            if (
                wrapperRef.current?.contains &&
                !wrapperRef.current.contains(event.target)
            ) {
                outsideClickAlert()
            }
        }
        // Bind
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            // dispose
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [wrapperRef, outsideClickAlert])
    return (
        <li className="mx-3" ref={elRef}>
            {!!items?.length && (
                // <span
                //     style={{ color: navColor, cursor: 'default' }}
                //     onMouseOver={handleMouseOver}
                // >
                //     <MdArrowDropDown className="inline-block" /> {label}
                // </span>
                <Link
                    href={link}
                    color={navColor}
                    hoverColor={navHoverColor}
                    onMouseOver={handleMouseOver}
                >
                    <MdArrowDropDown className="inline-block" /> {label}
                </Link>
            )}
            {!items?.length && (
                <Link
                    href={link}
                    color={navColor}
                    hoverColor={navHoverColor}
                    onMouseOver={handleMouseOver}
                >
                    {Boolean(items?.length) && (
                        <MdArrowDropDown className="inline-block" />
                    )}{' '}
                    {label}
                </Link>
            )}

            {Boolean(items?.length) && depth === 1 && (
                <div className="relative">
                    <WatefallMenu
                        style={{
                            // left: elRef.current?.offsetLeft ?? 0,
                            width: 'max-content'
                        }}
                        ref={wrapperRef}
                        visible={isHovering}
                        items={items}
                        onFocus={() => {
                            setIsHovering(true)
                        }}
                        onBlur={() => {
                            setIsHovering(false)
                        }}
                    />
                </div>
            )}
            {Boolean(items?.length) && depth > 1 && (
                <MegaMenu
                    ref={wrapperRef}
                    visible={isHovering}
                    onFocus={() => {
                        setIsHovering(true)
                    }}
                    onBlur={() => {
                        // setIsHovering(false)
                    }}
                    menuData={items}
                />
            )}
        </li>
    )
}

const WatefallMenu = forwardRef(
    ({ items, visible = false, onBlur, onFocus, style }, ref) => {
        const csss = css`
            position: absolute;
            display: flex;
            margin-left: auto;
            margin-right: auto;
            top: 0;
            left: 0;
            right: 0;
            z-index: 9;
            .card {
                border-radius: 0.5rem;
                border-top-right-radius: 0;
                border-top-left-radius: 0;
            }
            .card-body {
                padding: 1rem;
            }
            .card-content {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }
        `
        if (!visible) return null
        return (
            <div
                style={style}
                ref={ref}
                className="container waterfall-menu"
                css={csss}
                onMouseOver={onFocus}
                onMouseOut={onBlur}
            >
                <Card bordered shadow="md" style="height: 100%;">
                    {items.map((item, idx) => (
                        <Link key={idx} href={item.link}>
                            <span className="mx-2">{item.label}</span>
                        </Link>
                    ))}
                </Card>
            </div>
        )
    }
)
